import React, { useEffect } from "react";
import ReactDOM from "react-dom";
import FocusLock from "react-focus-lock";
import CloseIcn from "@assets/icons/rounded/close.svg";

import styles from "./modal-dialog-component.module.scss";
import { Role, ModalVariant } from ".";
import { IconComponent } from "../icon";
import clsx from "clsx";

export interface IModalDialogComponentProps {
  ariaLabel: string;
  background: string;
  children: JSX.Element;
  modalRef: React.RefObject<HTMLDivElement>;
  onClickAway: (e: any) => void;
  onClose: () => void;
  onKeyDown: ({ key }: KeyboardEvent) => void;
  role: Role;
  title?: string;
  variant: ModalVariant;
  width?: number;
}

const ModalDialogComponent = (props: IModalDialogComponentProps) => {
  useEffect(() => {
    window.addEventListener("keydown", props.onKeyDown);
    document.documentElement.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = "auto";
      if (!!window) {
        window.removeEventListener("keydown", props.onKeyDown);
      }
    };
  }, []);

  const modalSize = getModalSize(props.variant);
  const maxHeight = props.variant === "full-screen" ? { maxHeight: "initial" } : "";
  const padding = props.variant === "full-screen" ? { padding: "0" } : "";

  if (typeof document !== "undefined") {
    return ReactDOM.createPortal(
      <FocusLock>
        <div
          className={styles["c-modal-cover"]}
          role={props.role}
          aria-label={props.ariaLabel}
          aria-modal="true"
          tabIndex={0}
          onMouseDown={(e): any => {
            props.onClickAway(e);
          }}
        >
          {props.variant === "full-screen" && (
            <button
              className={clsx(styles["c-modal__close"], styles.close)}
              aria-labelledby="close-modal"
              onClick={props.onClose}
              type="button"
            >
              <IconComponent icon={CloseIcn} size="24px" fillColor="currentColor" />
            </button>
          )}
          <div
            className={styles["c-modal"]}
            style={{
              background: props.background,
              width: modalSize,
              ...maxHeight,
              overflow: "initial"
            }}
            ref={props.modalRef}
          >
            {props.variant !== "full-screen" && (
              <div role="button" className={styles.close} onClick={props.onClose}>
                <IconComponent icon={CloseIcn} size="24px" fillColor="currentColor" />
              </div>
            )}

            <div className={styles["c-modal__body"]} style={{ ...padding }}>
              {props.title && <h2>{props.title}</h2>}
              <div className={styles.modalContent}>{props.children ? props.children : ""}</div>
            </div>
          </div>
        </div>
      </FocusLock>,
      document.body
    );
  }

  return null;
};

export { ModalDialogComponent };

const getModalSize = (variant: ModalVariant): string => {
  switch (variant) {
    case "small":
      return "600px";
    case "big":
      return "50%";
    case "full-screen":
      return "80%";
    default:
      return "600px";
  }
};
